import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Stack, Container } from "@mui/material";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple, faGooglePlay } from "@fortawesome/free-brands-svg-icons";

// Simplified word rotation component without complex animations
const WordRotator = ({ words, interval = 2000 }) => {
  const [currentWord, setCurrentWord] = useState(words[0]);
  const [index, setIndex] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      const nextIndex = (index + 1) % words.length;
      setIndex(nextIndex);
      setCurrentWord(words[nextIndex]);
    }, interval);
    
    return () => clearInterval(timer);
  }, [index, words, interval]);
  
  return (
    <Box 
      component="span" 
      sx={{ 
        color: "#7F9BFF",
        display: "block",
        minHeight: "1.2em", // Ensure consistent height
        position: "relative"
      }}
    >
      {currentWord}
    </Box>
  );
};

// Background glow element
const GlowElement = ({ color, ...props }) => (
  <Box
    sx={{
      position: "absolute",
      width: "25vw",
      height: "25vw",
      borderRadius: "50%",
      background: color,
      filter: "blur(80px)",
      opacity: 0.4,
      zIndex: 0,
    }}
    {...props}
  />
);

export default function HeroSection() {
  return (
    <Box
      id="home"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        background: "linear-gradient(135deg, #121212, #1E1E38)",
        color: "white",
        overflow: "hidden",
      }}
    >
      {/* Background elements */}
      <GlowElement color="rgba(109, 131, 242, 0.6)" sx={{ top: "10%", left: "-5%" }} />
      <GlowElement color="rgba(255, 215, 0, 0.2)" sx={{ bottom: "-10%", right: "15%" }} />
      <GlowElement color="rgba(138, 43, 226, 0.3)" sx={{ top: "30%", right: "-5%" }} />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 6, md: 2 },
            py: { xs: 10, md: 0 },
          }}
        >
          {/* Left Side: Content */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{
              width: { xs: "100%", md: "45%" },
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#6d83f2",
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                fontSize: { xs: "0.7rem", md: "0.9rem" },
              }}
            >
              NEXT GENERATION TRACKING APP
            </Typography>

            <Typography
              variant="h1"
              fontWeight="800"
              sx={{
                mb: 0.5,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                lineHeight: 1.2,
              }}
            >
              First{" "}
              <Box
                component="span"
                sx={{
                  color: "#FFD700",
                  fontWeight: 900,
                }}
              >
                AI-Powered
              </Box>{" "}
              Tracker
            </Typography>
            
            <Typography
              variant="h1"
              fontWeight="800"
              sx={{
                mb: 2,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                lineHeight: 1.2,
              }}
            >
              for <WordRotator words={["Athletes", "Scholars", "Creators", "Entrepreneurs", "Gamers"]} />
            </Typography>

            <Typography
              variant="body1"
              sx={{
                opacity: 0.8,
                fontSize: { xs: "1rem", md: "1.1rem" },
                maxWidth: { xs: "100%", md: "90%" },
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Track habits, verify achievements with AI, challenge friends,
              and build unbreakable streaks that transform your life.
            </Typography>

            {/* App Store Buttons */}
            <Stack 
              direction={{ xs: "column", sm: "row" }} 
              spacing={2} 
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                component="a"
                href="https://testflight.apple.com/join/Nkn4cBdH"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={
                  <FontAwesomeIcon icon={faApple} style={{ fontSize: "1.2rem" }} />
                }
                sx={{
                  backgroundColor: "#FFFFFF",
                  color: "#121212",
                  borderRadius: "12px",
                  px: { xs: 3, md: 4 },
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "0 8px 20px rgba(109, 131, 242, 0.2)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    backgroundColor: "#F8F8F8",
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 25px rgba(109, 131, 242, 0.3)",
                  },
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                Download Beta
              </Button>

              <Button
                startIcon={<FontAwesomeIcon icon={faGooglePlay} style={{ fontSize: "1.2rem" }} />}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  color: "#FFFFFF",
                  borderRadius: "12px",
                  px: { xs: 3, md: 4 },
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  },
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                Coming Soon
              </Button>
            </Stack>
            
            {/* Metrics */}
            <Box 
              sx={{ 
                mt: 5,
                display: "flex", 
                flexWrap: "wrap",
                gap: 4,
                justifyContent: { xs: "center", md: "flex-start" }
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight="700" sx={{ color: "#6d83f2" }}>10K+</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>Beta Users</Typography>
              </Box>
              
              <Box>
                <Typography variant="h4" fontWeight="700" sx={{ color: "#6d83f2" }}>95%</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>Completion Rate</Typography>
              </Box>
              
              <Box>
                <Typography variant="h4" fontWeight="700" sx={{ color: "#6d83f2" }}>4.9</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>App Rating</Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Side: Phone Image */}
          <Box
            sx={{
              width: { xs: "100%", md: "55%" },
              position: "relative",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {/* Subtle glow behind the image */}
            <Box
              sx={{
                position: "absolute",
                width: "80%",
                height: "40%",
                background: "radial-gradient(circle, rgba(109, 131, 242, 0.3) 0%, rgba(109, 131, 242, 0) 70%)",
                bottom: "10%",
                borderRadius: "50%",
                filter: "blur(40px)",
                zIndex: 0,
              }}
            />
            
            <motion.img
              src="/3phones-min.png"
              alt="App Screenshots"
              loading="lazy"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              style={{
                maxWidth: "100%",
                height: "auto",
                filter: "drop-shadow(0 30px 40px rgba(0,0,0,0.3))",
                position: "relative",
                zIndex: 1
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}